// components/PropertyCalendar.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import API_URL from '../config';
import PlatformBadge from './PlatformBadge';
import { getAuthToken, getAuthUser } from '../utils/authSession';
import { cacheGet, cacheSet } from '../utils/dataCache';
import { validateRequiredFields, clearFieldInvalid } from '../utils/formValidation';
import './PropertyCalendar.css';
import { sortedNames } from '../utils/sortNames';
import useMonthSwipe from '../utils/useMonthSwipe';

// Eigen maandweergave i.p.v. FullCalendar (zie het lange commentaar hoger op
// in PropertyCalendar.css bij ".month-view") - hieronder wat kleine
// datum-helpers die daarvoor nodig zijn.
const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const addMonths = (date, months) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  return d;
};
const startOfMonth = (date) => {
  const d = startOfDay(date);
  d.setDate(1);
  return d;
};
// 0 = maandag, ..., 6 = zondag (i.p.v. JS' eigen 0=zondag) - onze weekrijen
// beginnen bewust op maandag, zoals de rest van de kalenders in de app al
// deden (FullCalendar toonde dat voorheen ook al zo via de nl/fr-locale).
const mondayIndex = (date) => (date.getDay() + 6) % 7;

// Alle weken (elk als hun maandag-startdatum) van een maand: altijd 6 rijen, zodat de
// kaart elke maand even hoog is (anders springt de hoogte bij het bladeren). De dagen
// van de vorige/volgende maand vullen de eerste/laatste week aan.
const buildWeeks = (anchor) => {
  const monthStart = startOfMonth(anchor);
  const gridStart = addDays(monthStart, -mondayIndex(monthStart));
  return Array.from({ length: 6 }, (_, i) => addDays(gridStart, i * 7));
};

const formatDateTime = (date) => new Date(date).toLocaleString('nl-NL', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
});
// Enkel het uur (geen datum) voor in de titel van een kalenderblokje zelf -
// de datum staat al af te lezen aan de kolom/dag waarin het blokje staat, dus
// die zou daar dubbel op zijn. Locale-bewust (NL/EN) net als de rest van de
// i18n-opzet.
const formatTimeOnly = (date, locale) => new Date(date).toLocaleString(locale, {
  hour: '2-digit', minute: '2-digit'
});

// Standaard schoonmaakduur in milliseconden voor het vooraf invullen van het
// inplan-formulier: gebruikt de "aantal uur voor schoonmaker" die de admin
// per woning instelde (cleaningHours), en valt enkel terug op een generieke
// duur wanneer dat veld niet is ingesteld. Voorheen stond dit hard op +2u/+3u
// vast, ongeacht wat er bij de woning zelf geconfigureerd stond - dat gaf een
// "Duur" bij Ingepland die niet overeenkwam met de effectief ingestelde uren.
const defaultCleaningDurationMs = (cleaningHours, fallbackHours) => {
  const hours = Number(cleaningHours);
  const validHours = !isNaN(hours) && hours > 0 ? hours : fallbackHours;
  return validHours * 60 * 60 * 1000;
};

function PropertyCalendar({ propertyId, propertyName, userRole, cleaningTier, cleaningHours, defaultCleanerCount, cleaningServices, onEditCleaning }) {
  const { t, i18n } = useTranslation();
  // Bij tier "NONE" doen wij nooit schoonmaak voor deze woning: de verhuurder
  // krijgt hier geen zelf-inplannen-flow te zien, enkel een verwijzing om ons
  // rechtstreeks te contacteren. Geldt enkel voor de verhuurder zelf - de
  // admin (die hier ook via zijn eigen kalender-drilldown kan binnenkomen)
  // ziet altijd alles, ongeacht de tier.
  const cleaningBlocked = userRole === 'VERHUURDER' && cleaningTier === 'NONE';
  const [events, setEvents] = useState(() => cacheGet(`calendar|${propertyId}`) || []);
  const [loading, setLoading] = useState(() => !cacheGet(`calendar|${propertyId}`));
  // Eerste dag van de zichtbare maand - vervangt FullCalendar's eigen
  // interne view-state nu we de maandweergave zelf tekenen.
  const [monthAnchor, setMonthAnchor] = useState(() => startOfMonth(new Date()));
  const [now, setNow] = useState(() => new Date());
  const [showCleaningModal, setShowCleaningModal] = useState(false);
  const [unscheduledBookings, setUnscheduledBookings] = useState([]);
  const [selectedBookingId, setSelectedBookingId] = useState('');
  const [bookingModalData, setBookingModalData] = useState(null);
  // Admin-only modal om een al ingepland schoonmaakmoment alsnog aan een
  // (nog ongeplande) boeking te koppelen of te ontkoppelen - zie
  // handleCleaningBarClick/handleLinkBooking hieronder. Bevat de taak-id en
  // het huidige bookingId (leeg = nog geen koppeling).
  const [linkBookingModalData, setLinkBookingModalData] = useState(null);
  // Eigen in-app melding/bevestiging i.p.v. window.alert()/window.confirm()
  // (die als generieke browserpopups oogden i.p.v. bij de SmartStays-app te
  // horen) - confirmState.onConfirm wordt uitgevoerd zodra de gebruiker in
  // het modaltje zelf bevestigt, niet synchroon zoals window.confirm() dat deed.
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [confirmState, setConfirmState] = useState(null);
  const [formData, setFormData] = useState({
    startDate: new Date(),
    endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
    notes: '',
    // Vrije tekst (bv. een toegangscode) - los van "notes", enkel zichtbaar
    // voor de schoonmaker die uiteindelijk effectief aangenomen wordt voor
    // deze taak (zie backend accessInfo-gating in /api/my-tasks).
    access: '',
    // Aantal schoonmakers dat voor DEZE taak verwacht wordt - enkel door de
    // admin aan te passen (bepaalt de "Duur"-verdeling/uitbetaling). Leeg =
    // valt terug op de standaard van de woning (defaultCleanerCount).
    cleanerCount: ''
  });

  const isEnglish = i18n.language?.startsWith('en');
  const isFrench = i18n.language?.startsWith('fr');
  const locale = isFrench ? 'fr-BE' : isEnglish ? 'en-GB' : 'nl-NL';

  // Elke minuut verversen zodat "vandaag" (en de rode/blauwe nadruk erop)
  // ook bijblijft als deze pagina lang openstaat - zelfde patroon als
  // PropertyTimeline.js.
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const goPrevMonth = () => setMonthAnchor((prev) => addMonths(prev, -1));
  const goNextMonth = () => setMonthAnchor((prev) => addMonths(prev, 1));
  const goCurrentMonth = () => setMonthAnchor(startOfMonth(new Date()));

  // Maandag-gebaseerde weekdag-labels (ma, di, wo, ...), taalbewust - een
  // willekeurige, gekende maandag (12 jan 2026) volstaat als referentie om
  // de 7 opeenvolgende korte weekdagnamen in de juiste taal op te vragen.
  const weekdayLabels = useMemo(() => {
    const referenceMonday = new Date(2026, 0, 12);
    return Array.from({ length: 7 }, (_, i) => addDays(referenceMonday, i).toLocaleDateString(locale, { weekday: 'short' }));
  }, [locale]);

  // Bladeren met de pijltjes of door te vegen: de vorige/volgende maand schuift er
  // aaneensluitend in (zie utils/useMonthSwipe.js).
  const swipe = useMonthSwipe({
    monthKey: `${monthAnchor.getFullYear()}-${monthAnchor.getMonth()}`,
    onNext: goNextMonth,
    onPrev: goPrevMonth,
  });

  const todayKey = new Date().toDateString();

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  };

  const askConfirm = (message, onConfirm) => setConfirmState({ message, onConfirm });
  // Louter informatief modaltje (enkel "Sluiten") - gebruikt wanneer een
  // verhuurder wel de details van een schoonmaakmoment mag zien, maar het
  // (nog) niet mag verwijderen; zie handleCleaningBarClick hieronder.
  const showInfo = (message) => setConfirmState({ message, onConfirm: null });
  // Eigen user-id van de ingelogde verhuurder, om te bepalen of hij/zij zelf
  // de "maker" (createdBy) van een schoonmaakmoment is.
  const currentUserId = JSON.parse(getAuthUser() || 'null')?.id;

  const fetchCalendarData = useCallback(async () => {
    if (!propertyId) return;

    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/calendar/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Nog niet ingepland (wacht op goedkeuring van de admin): nog niet in de kalender.
      const formattedEvents = response.data.filter(item => !(item.type === 'cleaning' && item.status === 'PENDING_APPROVAL')).map(item => {
        if (item.type === 'booking') {
          // Originele check-in/check-out (met uur) bewaren voor de tekst/tooltip.
          const originalCheckIn = new Date(item.checkIn);
          const originalCheckOut = new Date(item.checkOut);

          // Enkel maandweergave (dayGridMonth) - die kan sowieso geen echt
          // uur-rooster tonen (elke dag is één rij), dus de echte tijden
          // dienen hier vooral om (a) de titel zelf de exacte in-/uitchecktijd
          // te laten tonen en (b) samen met eventOrder="start" hieronder de
          // volgorde binnen eenzelfde dag correct te laten lopen: uitchecken
          // van boeking A → schoonmaak → inchecken van boeking B, in plaats
          // van een willekeurige/database-volgorde die als overlappend aanvoelt.
          const visualStart = originalCheckIn;
          const visualEnd = originalCheckOut;

          // "bookingSource" is het échte kanaal waarop de gast oorspronkelijk
          // boekte (Airbnb/Booking.com/Direct/...), meegeleverd door Guesty's
          // echte Open API-webhooks (zie backend/index.js). "platform" is
          // enkel onze eigen sync-koppeling (bv. altijd "GUESTY" voor alles
          // wat via Guesty binnenkomt, ook al kwam de boeking oorspronkelijk
          // van Airbnb). Toon het échte kanaal als we het weten, anders val
          // terug op platform - zo blijft de kalendertekst altijd zinvol.
          const displayPlatform = item.bookingSource || item.platform;
          const locale = isFrench ? 'fr-BE' : isEnglish ? 'en-GB' : 'nl-NL';

          return {
            id: String(item.id),
            title: `${item.guestName || t('calendar.guest')} - ${displayPlatform} (${formatTimeOnly(originalCheckIn, locale)} → ${formatTimeOnly(originalCheckOut, locale)})`,
            start: visualStart,
            end: visualEnd,
            allDay: false,
            backgroundColor: '#0d47a1',
            borderColor: '#0d47a1',
            textColor: 'white',
            extendedProps: {
              type: 'booking',
              dbId: item.id,
              originalCheckIn,
              originalCheckOut,
              platform: item.platform,
              bookingSource: item.bookingSource,
              displayPlatform,
              guestName: item.guestName,
              guestEmail: item.guestEmail,
              guestAccessCode: item.guestAccessCode,
              accessCodeSentAt: item.accessCodeSentAt
            }
          };
        } else {
          const cleaningStart = new Date(item.startDate);
          const cleaningEnd = new Date(item.endDate);
          const locale = isFrench ? 'fr-BE' : isEnglish ? 'en-GB' : 'nl-NL';
          // Lichtrood zolang er nog niemand (definitief) is ingepland,
          // groen zodra de admin een schoonmaker heeft bevestigd - en groen
          // blijft die schoonmaak ook nadat die voltooid en uitbetaald is
          // (INVOICED), anders springt hij hier terug naar rood. Een
          // schoonmaakmoment dat al helemaal in het verleden ligt tonen we
          // hier ALTIJD groen, ongeacht de status: in de praktijk is die dan
          // al lang toegewezen/uitgevoerd geweest.
          // Zelfde kleuren als de Multikalender: groen = voltooid/verstreken,
          // blauw = volledig bezet, oranje = nog niet alle schoonmakers
          // aangemeld, rood = nog niemand.
          const isDone = cleaningEnd < new Date() || ['COMPLETED', 'INVOICED'].includes(item.status);
          // "expected_cleaner_count" = het MINIMUM aantal schoonmakers.
          // Rood = niemand toegewezen, oranje = minimum nog niet bereikt.
          const hasCleaner = item.status === 'ASSIGNED' || Number(item.assigned_cleaner_count) > 0;
          const isPartial = Number(item.assigned_cleaner_count) < Number(item.expected_cleaner_count);
          // Enkel de admin ziet de interne tussenstappen (rood/oranje). Een
          // verhuurder ziet alleen: blauw = komt nog, groen = voltooid.
          const cleaningColor = isDone
            ? '#4caf50'
            : (userRole !== 'ADMIN' ? '#1e88e5' : (hasCleaner ? (isPartial ? '#f59e0b' : '#1e88e5') : '#ef5350'));

          return {
            id: String(item.id),
            title: `${item.title} (${formatTimeOnly(cleaningStart, locale)} → ${formatTimeOnly(cleaningEnd, locale)})`,
            start: cleaningStart,
            end: cleaningEnd,
            allDay: false,
            backgroundColor: cleaningColor,
            borderColor: cleaningColor,
            textColor: 'white',
            extendedProps: {
              type: 'cleaning',
              dbId: item.id,
              description: item.description,
              cleanerName: item.cleaner_name,
              cleaner2Name: item.cleaner2_name,
              cleaner3Name: item.cleaner3_name,
              status: item.status,
              assignedCleanerCount: item.assigned_cleaner_count,
              expectedCleanerCount: item.expected_cleaner_count,
              // Enkel nodig om (client-side, voor de UI) te bepalen of de
              // ingelogde verhuurder dit moment nog zelf mag verwijderen -
              // zie canDeleteCleaning/handleCleaningBarClick hieronder. De
              // échte grens ligt bij dezelfde regel in backend/index.js.
              createdBy: item.createdBy,
              cleanerId: item.cleanerId,
              cleanerId2: item.cleanerId2,
              cleanerId3: item.cleanerId3,
              startDate: item.startDate,
              // Enkel nodig voor het admin-only "koppel aan boeking"-modal
              // hieronder (zie handleCleaningBarClick/linkBookingModalData).
              bookingId: item.bookingId
            }
          };
        }
      });

      setEvents(formattedEvents);
      cacheSet(`calendar|${propertyId}`, formattedEvents);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij ophalen kalender:', error);
      setLoading(false);
    }
  }, [propertyId, t, isEnglish, isFrench]);

  const fetchUnscheduledBookings = useCallback(async () => {
    if (!propertyId) return;

    try {
      const token = getAuthToken();
      const response = await axios.get(`${API_URL}/api/unscheduled-bookings`, {
        params: { propertyId },
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnscheduledBookings(response.data);
    } catch (error) {
      console.error('Fout bij laden niet-ingeplande boekingen:', error);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchCalendarData();
    fetchUnscheduledBookings();
  }, [fetchCalendarData, fetchUnscheduledBookings]);

  const handleOpenCleaningModal = (bookingId = '') => {
    setSelectedBookingId(bookingId);
    const booking = bookingId ? unscheduledBookings.find(b => b.id === bookingId) : null;
    const seededCleanerCount = defaultCleanerCount ?? '';
    // Standaard de eerste schoonmaakdienst van de woning (PropertyCleaningService,
    // zelfde volgorde als de dropdown bij "In te plannen" in het beheerpaneel) -
    // blijft gewoon via de dropdown hieronder aanpasbaar. Geen diensten? Dan
    // valt dit gewoon terug op lege string (= legacy vlakke velden van de woning).
    const seededServiceId = cleaningServices?.[0]?.id || '';
    if (booking) {
      const start = new Date(booking.checkOut);
      start.setHours(start.getHours() + 1);
      const end = new Date(start.getTime() + defaultCleaningDurationMs(cleaningHours, 3));
      setFormData({ startDate: start, endDate: end, notes: '', access: '', cleanerCount: seededCleanerCount, cleaningServiceId: seededServiceId });
    } else {
      const start = new Date();
      setFormData({
        startDate: start,
        endDate: new Date(start.getTime() + defaultCleaningDurationMs(cleaningHours, 2)),
        notes: '',
        access: '',
        cleanerCount: seededCleanerCount,
        cleaningServiceId: seededServiceId
      });
    }
    setShowCleaningModal(true);
  };

  const handleSelectBooking = (bookingId) => {
    setSelectedBookingId(bookingId);
    const booking = unscheduledBookings.find(b => b.id === bookingId);
    if (booking) {
      const start = new Date(booking.checkOut);
      start.setHours(start.getHours() + 1);
      const end = new Date(start.getTime() + defaultCleaningDurationMs(cleaningHours, 3));
      setFormData(prev => ({ ...prev, startDate: start, endDate: end }));
    }
  };

  const handleSaveCleaning = async (e) => {
    e.preventDefault();

    if (!validateRequiredFields(e.target)) {
      showToast(t('common.fillRequired'), 'error');
      return;
    }

    if (formData.startDate >= formData.endDate) {
      showToast(t('calendar.endAfterStart'), 'error');
      return;
    }

    try {
      const token = getAuthToken();

      const response = await axios.post(`${API_URL}/api/cleaning-tasks`, {
        propertyId,
        bookingId: selectedBookingId || undefined,
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
        notes: formData.notes,
        cleaningServiceId: formData.cleaningServiceId || undefined,
        // Enkel relevant/toegelaten voor een admin - de backend negeert dit
        // sowieso voor een verhuurder, maar niet meesturen als het veld hier
        // niet eens getoond wordt.
        ...(userRole === 'ADMIN' ? { expectedCleanerCount: formData.cleanerCount || undefined } : {})
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Een verhuurder-toegevoegde taak komt niet meteen open te staan, maar
      // wacht eerst op goedkeuring door een admin - andere melding dan bij
      // een meteen-bevestigde (admin-)taak, zodat dit duidelijk is.
      showToast(
        response.data.task?.status === 'PENDING_APPROVAL' ? t('calendar.cleaningPendingApproval') : t('calendar.cleaningScheduled'),
        'success'
      );

      resetForm();
      fetchCalendarData();
      fetchUnscheduledBookings();
    } catch (error) {
      console.error('Fout bij opslaan:', error);
      showToast(error.response?.data?.error || t('calendar.saveError'), 'error');
    }
  };

  // Bevestiging gebeurt al bij de aanroeper (handleCleaningBarClick) via
  // askConfirm() - geen aparte window.confirm()-guard hier meer nodig.
  const handleDeleteCleaning = async (taskId) => {
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/api/cleaning-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(t('calendar.cleaningDeleted'), 'success');
      fetchCalendarData();
      fetchUnscheduledBookings();
    } catch (error) {
      console.error('Fout bij verwijderen:', error);
      showToast(t('calendar.deleteFailed'), 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      startDate: new Date(),
      endDate: new Date(new Date().setHours(new Date().getHours() + 2)),
      notes: '',
      access: '',
      cleanerCount: ''
    });
    setSelectedBookingId('');
    setShowCleaningModal(false);
  };

  const statusLabels = {
    OPEN: t('calendar.statusOpen'),
    PENDING_CONFIRMATION: t('calendar.statusPending'),
    ASSIGNED: t('calendar.statusAssigned'),
    COMPLETED: t('calendar.statusCompleted'),
    CANCELLED: t('calendar.statusCancelled')
  };

  // Klik op een boeking-balkje: toont de alleen-lezen boekingdetails-modal
  // (zelfde gegevens/flow als voorheen via FullCalendar's eventClick, nu
  // rechtstreeks op ons eigen event-object i.p.v. via clickInfo.event).
  const handleBookingBarClick = (event) => {
    const props = event.extendedProps;
    const hasNoCleaning = unscheduledBookings.some((b) => b.id === props.dbId);

    setBookingModalData({
      dbId: props.dbId,
      platform: props.platform,
      displayPlatform: props.displayPlatform || props.platform,
      guestName: props.guestName,
      guestEmail: props.guestEmail,
      guestAccessCode: props.guestAccessCode,
      accessCodeSentAt: props.accessCodeSentAt,
      checkIn: props.originalCheckIn || event.start,
      checkOut: props.originalCheckOut || event.end,
      hasNoCleaning
    });
  };

  // Zelfde regel als canDeleteCleaning in PropertyTimeline.js (en de échte
  // grens in backend/index.js): een verhuurder mag enkel zelf verwijderen
  // wat hij zelf aanmaakte, nog niet aanvaard/toegewezen is, en nog niet in
  // het verleden ligt.
  const canDeleteCleaning = (props) => {
    if (userRole === 'ADMIN') return true;
    if (userRole !== 'VERHUURDER') return false;
    const hasCleaner = Boolean(props.cleanerId || props.cleanerId2 || props.cleanerId3);
    const isOwnCreation = props.createdBy === currentUserId;
    const isStillPending = props.status === 'PENDING_APPROVAL';
    const isInFuture = new Date(props.startDate) > new Date();
    return isOwnCreation && isStillPending && !hasCleaner && isInFuture;
  };

  const handleCleaningBarClick = (event) => {
    const props = event.extendedProps;
    // Een admin krijgt hier altijd het "koppel aan boeking"-modal (dat ook
    // de info toont én, indien toegelaten, een verwijderknop heeft) i.p.v.
    // het gewone info/verwijder-bevestigingstekstje hieronder - enkel voor
    // ADMIN, een verhuurder ziet dit niet.
    if (userRole === 'ADMIN') {
      setLinkBookingModalData({
        taskId: props.dbId,
        bookingId: props.bookingId || '',
        start: event.start,
        end: event.end,
        status: props.status,
        cleanerNames: sortedNames([props.cleanerName, props.cleaner2Name, props.cleaner3Name]).join(', ')
      });
      return;
    }

    let info = t('calendar.cleaningInfo', {
      start: formatTimeOnly(event.start, locale),
      end: formatTimeOnly(event.end, locale)
    });
    const cleanerNames = sortedNames([props.cleanerName, props.cleaner2Name, props.cleaner3Name]).join(', ');
    if (cleanerNames) {
      info += `\n${t('calendar.cleaner')}: ${cleanerNames}`;
    }
    if (canDeleteCleaning(props)) {
      askConfirm(`${info}\n\n${t('calendar.deleteCleaningPrompt')}`, () => handleDeleteCleaning(props.dbId));
    } else {
      showInfo(info);
    }
  };

  // Koppelt (of ontkoppelt, bij bookingId === '') een al bestaand
  // schoonmaakmoment aan een boeking - admin-only, zie linkBookingModalData.
  // Dezelfde validatie (hoort bij dezelfde woning, nog niet aan een andere
  // taak gekoppeld) gebeurt server-side in PUT /api/cleaning-tasks/:id.
  const handleLinkBooking = async (taskId, bookingId) => {
    try {
      const token = getAuthToken();
      await axios.put(`${API_URL}/api/cleaning-tasks/${taskId}`, {
        bookingId: bookingId || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast(t('calendar.bookingLinkSaved'), 'success');
      setLinkBookingModalData(null);
      fetchCalendarData();
      fetchUnscheduledBookings();
    } catch (error) {
      console.error('Fout bij koppelen boeking:', error);
      showToast(error.response?.data?.error || t('calendar.saveError'), 'error');
    }
  };

  const closeBookingModal = () => {
    setBookingModalData(null);
  };

  if (loading) {
    return <div className="calendar-loading" aria-busy="true" />;
  }

  // 1 maand: titel + weekrijen. Wordt 3x gerenderd (vorige/huidige/volgende maand).
  const renderMonth = (anchor) => (
    <>
      <div className="pc-month-title">{anchor.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}</div>
      <div className="month-view">
        <div className="month-weekday-header">
          {weekdayLabels.map((label) => (
            <div key={label} className="month-weekday-label">{label}</div>
          ))}
        </div>

        {buildWeeks(anchor).map((weekStart) => {
          const weekEnd = addDays(weekStart, 7);
          const weekBookings = events.filter((ev) => ev.extendedProps.type === 'booking'
            && new Date(ev.end) > weekStart && new Date(ev.start) < weekEnd);
          const weekCleanings = events.filter((ev) => ev.extendedProps.type === 'cleaning'
            && new Date(ev.end) > weekStart && new Date(ev.start) < weekEnd);

          const barStyle = (evStart, evEnd) => {
            const s = new Date(evStart);
            const e = new Date(evEnd);
            const clampedStart = s < weekStart ? weekStart : s;
            const clampedEnd = e > weekEnd ? weekEnd : e;
            const weekMs = 7 * 24 * 60 * 60 * 1000;
            const leftPct = ((clampedStart - weekStart) / weekMs) * 100;
            const rawWidthPct = ((clampedEnd - clampedStart) / weekMs) * 100;
            return { left: `${leftPct}%`, width: `${Math.max(rawWidthPct, 1.5)}%` };
          };

          return (
            <div className="month-week-row" key={weekStart.toISOString()}>
              <div className="month-week-daynums">
                {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day) => (
                  <div
                    key={day.toISOString()}
                    className={`month-week-daynum ${day.getMonth() === anchor.getMonth() ? 'is-current-month' : ''} ${day.toDateString() === todayKey ? 'is-today' : ''}`}
                  >
                    {day.getDate()}
                  </div>
                ))}
              </div>

              <div className="month-week-track">
                {Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)).map((day, idx) => (
                  <div
                    key={day.toISOString()}
                    className={`month-week-gridline ${day.toDateString() === todayKey ? 'is-today' : ''}`}
                    style={{ left: `${(idx / 7) * 100}%`, width: `${(1 / 7) * 100}%` }}
                  />
                ))}

                {weekBookings.map((booking) => {
                  const { left, width } = barStyle(booking.start, booking.end);
                  const displayPlatform = booking.extendedProps.displayPlatform || booking.extendedProps.platform;
                  // Een boeking die de weekgrens overschrijdt, krijgt hier een
                  // apart balkje per weekrij (zie barStyle hierboven, dat elke
                  // rij op haar eigen weekStart/weekEnd clampt). Zonder deze
                  // check herhaalde het volledige label (logo+tijd+gastnaam)
                  // zich op élke rij waar de boeking doorheen liep - hier
                  // tonen we het enkel op de allereerste rij (waar de échte
                  // check-in valt), de vervolgrijen krijgen enkel de kale balk.
                  const startsHere = new Date(booking.extendedProps.originalCheckIn || booking.start) >= weekStart;
                  const endsHere = new Date(booking.extendedProps.originalCheckOut || booking.end) <= weekEnd;
                  return (
                    <div
                      key={`booking-${booking.id}`}
                      className={`month-bar month-bar-booking ${/vrbo|homeaway/i.test(String(displayPlatform || '')) ? 'is-wide' : ''} ${startsHere ? '' : 'is-cut-left'} ${endsHere ? '' : 'is-cut-right'}`}
                      style={{ left: `min(${left}, calc(100% - var(--min-booking-bar)))`, width }}
                      title={`${booking.extendedProps.guestName || t('calendar.guest')} - ${displayPlatform}\n${formatDateTime(booking.extendedProps.originalCheckIn || booking.start)} → ${formatDateTime(booking.extendedProps.originalCheckOut || booking.end)}`}
                      onClick={() => handleBookingBarClick(booking)}
                    >
                      {startsHere && (
                        <PlatformBadge platform={displayPlatform} className="platform-badge-only" />
                      )}
                    </div>
                  );
                })}

                {weekCleanings.map((cleaning) => {
                  const { left, width } = barStyle(cleaning.start, cleaning.end);
                  const isPast = new Date(cleaning.end) < now;
                  // 3 kleuren i.p.v. 2: rood zolang er nog geen schoonmaker
                  // toegewezen is (Ingepland), oranje zodra die er wel is maar
                  // de schoonmaak nog niet voltooid is (Toegewezen), groen
                  // zodra voltooid/uitbetaald of al verstreken (Voltooid).
                  const isCompleted = isPast || ['COMPLETED', 'INVOICED'].includes(cleaning.extendedProps.status);
                  const cleaningStatusClass = isCompleted
                    ? 'month-bar-cleaning-completed'
                    // Verhuurder: enkel blauw (komt nog) of groen (voltooid).
                    : userRole !== 'ADMIN' ? 'month-bar-cleaning-assigned'
                    // Admin: rood = niemand toegewezen, oranje = minimum aantal schoonmakers niet bereikt.
                    : ((cleaning.extendedProps.status === 'ASSIGNED' || Number(cleaning.extendedProps.assignedCleanerCount) > 0) ? (Number(cleaning.extendedProps.assignedCleanerCount) < Number(cleaning.extendedProps.expectedCleanerCount) ? 'month-bar-cleaning-partial' : 'month-bar-cleaning-assigned') : 'month-bar-cleaning-unassigned');
                  return (
                    <div
                      key={`cleaning-${cleaning.id}`}
                      className={`month-bar month-bar-cleaning ${cleaningStatusClass}`}
                      style={{ left, width }}
                      title={`${cleaning.title}\n${formatDateTime(cleaning.start)} → ${formatDateTime(cleaning.end)}`}
                      onClick={() => handleCleaningBarClick(cleaning)}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  return (
    <div className="property-calendar">
      <div className="calendar-header">
        <h3>{t('calendar.title')} - {propertyName}</h3>
        {!cleaningBlocked && (
          <button onClick={() => handleOpenCleaningModal()} className="add-cleaning-btn">
            + {t('calendar.addCleaning')}
          </button>
        )}
      </div>

      <div className="pc-nav-row">
        <button type="button" className="pc-nav-btn" onClick={() => swipe.slide(-1)} aria-label={t('calendar.previous')}>&#8249;</button>
        <button type="button" className="pc-nav-btn" onClick={() => swipe.slide(1)} aria-label={t('calendar.next')}>&#8250;</button>
        <button type="button" className="pc-nav-today" onClick={goCurrentMonth}>{t('calendar.today')}</button>
      </div>

      {/* Eigen maandweergave: 1 rij per week, onder mekaar gestapeld, met
          boekingen/schoonmaak als percentage-gepositioneerde balkjes op hun
          échte tijdstip - zie het uitgebreide commentaar in
          PropertyCalendar.css bij ".month-view" voor de reden (geen
          FullCalendar "+N meer"-overflow meer, geen dubbel tijd-label).
          Vorige/huidige/volgende maand staan naast elkaar in 1 "track" die
          je kunt wegvegen. */}
      <div className="pc-swipe-card" ref={swipe.cardRef} {...swipe.handlers}>
        <div className="pc-swipe-track" ref={swipe.trackRef}>
          <div className="pc-swipe-pane pc-swipe-pane-prev" aria-hidden="true">{renderMonth(addMonths(monthAnchor, -1))}</div>
          <div className="pc-swipe-pane pc-swipe-pane-current">{renderMonth(monthAnchor)}</div>
          <div className="pc-swipe-pane pc-swipe-pane-next" aria-hidden="true">{renderMonth(addMonths(monthAnchor, 1))}</div>
        </div>
      </div>

      {/* Modal voor schoonmaak inplannen (altijd gekoppeld aan een boeking) */}
      {showCleaningModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal cleaning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('calendar.scheduleCleaning')}</h2>
              <button className="modal-close" onClick={resetForm}>✖</button>
            </div>
            <form onSubmit={handleSaveCleaning} noValidate onInput={clearFieldInvalid}>
              <div className="form-group">
                <label>{t('calendar.bookingOptional')}</label>
                <select
                  value={selectedBookingId}
                  onChange={(e) => handleSelectBooking(e.target.value)}
                >
                  <option value="">{t('calendar.noBookingOption')}</option>
                  {unscheduledBookings.map(booking => (
                    <option key={booking.id} value={booking.id}>
                      {booking.guestName || t('calendar.guest')} ({new Date(booking.checkIn).toLocaleDateString('nl-NL')} - {new Date(booking.checkOut).toLocaleDateString('nl-NL')})
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>{t('calendar.start')} *</label>
                  <DatePicker
                    selected={formData.startDate}
                    onChange={(date) => setFormData(prev => {
                      const duration = prev.endDate.getTime() - prev.startDate.getTime();
                      return { ...prev, startDate: date, endDate: new Date(date.getTime() + duration) };
                    })}
                    showTimeSelect
                    dateFormat="dd/MM/yyyy HH:mm"
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    className="datetime-picker"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('calendar.end')} *</label>
                  <DatePicker
                    selected={formData.endDate}
                    onChange={(date) => setFormData({...formData, endDate: date})}
                    showTimeSelect
                    dateFormat="dd/MM/yyyy HH:mm"
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    className="datetime-picker"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>{t('calendar.notes')}</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder={t('calendar.notesPlaceholder')}
                  rows="3"
                />
              </div>


              {cleaningServices && cleaningServices.length > 0 && (
                <div className="form-group">
                  <label>{t('admin.cleaningServiceLabel')}</label>
                  <select
                    value={formData.cleaningServiceId || ''}
                    onChange={(e) => setFormData({ ...formData, cleaningServiceId: e.target.value })}
                  >
                    {cleaningServices.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {userRole === 'ADMIN' && (
                <div className="form-group">
                  <label>{t('admin.expectedCleanerCountLabel')}</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={formData.cleanerCount}
                    onChange={(e) => setFormData({ ...formData, cleanerCount: e.target.value })}
                  />
                  <p className="form-hint">{t('admin.expectedCleanerCountHint')}</p>
                </div>
              )}

              <div className="modal-buttons">
                <button type="button" onClick={resetForm} className="cancel-btn">
                  {t('common.cancel')}
                </button>
                <button type="submit" className="submit-btn">
                  {t('calendar.schedule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal met boekingdetails (alleen-lezen: naam/e-mail komen 1-op-1 uit
          de gekoppelde bron - iCal-feed of, indien gekoppeld, Guesty's echte
          Open API - en worden hier niet meer handmatig overschreven). */}
      {bookingModalData && (
        <div className="modal-overlay" onClick={closeBookingModal}>
          <div className="modal booking-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><PlatformBadge platform={bookingModalData.displayPlatform} />{bookingModalData.guestName || t('calendar.guest')} - {bookingModalData.displayPlatform}</h2>
              <button className="modal-close" onClick={closeBookingModal}>✖</button>
            </div>

            <div className="booking-info-body">
              <p><strong>{t('calendar.start')}:</strong> {formatDateTime(bookingModalData.checkIn)}</p>
              <p><strong>{t('calendar.end')}:</strong> {formatDateTime(bookingModalData.checkOut)}</p>
              <p><strong>{t('calendar.guestName')}:</strong> {bookingModalData.guestName || t('calendar.guestInfoMissing')}</p>
              <p><strong>{t('calendar.guestEmail')}:</strong> {bookingModalData.guestEmail || t('calendar.guestInfoMissing')}</p>
              {/* Klant zonder schoonmaak: nergens iets over schoonmaak tonen. */}
              {!cleaningBlocked && (
                <p><strong>{t('calendar.cleaningLabel')}:</strong> {bookingModalData.hasNoCleaning ? t('calendar.cleaningNotPlanned') : t('calendar.cleaningPlanned')}</p>
              )}
            </div>

            {bookingModalData.hasNoCleaning && !cleaningBlocked && (
              <div className="booking-info-cleaning-section">
                <button
                  type="button"
                  className="submit-btn"
                  onClick={() => {
                    const dbId = bookingModalData.dbId;
                    closeBookingModal();
                    handleOpenCleaningModal(dbId);
                  }}
                >
                  {t('calendar.scheduleCleaningNow')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`toast-message ${toast.type}`}>{toast.message}</div>
      )}

      {/* Eigen bevestigingsmodaltje i.p.v. window.confirm() - askConfirm()
          hierboven vult confirmState.message/onConfirm; "Verwijderen" voert
          onConfirm uit en sluit het modaltje. */}
      {confirmState && (
        <div className="modal-overlay" onClick={() => setConfirmState(null)}>
          <div className="modal confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{confirmState.onConfirm ? t('common.confirm') : t('calendar.cleaningInfoTitle')}</h2>
              <button className="modal-close" onClick={() => setConfirmState(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>{confirmState.message}</p>
            </div>
            <div className="modal-buttons">
              {confirmState.onConfirm ? (
                <>
                  <button type="button" className="cancel-btn" onClick={() => setConfirmState(null)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={() => { const { onConfirm } = confirmState; setConfirmState(null); onConfirm(); }}
                  >
                    {t('common.delete')}
                  </button>
                </>
              ) : (
                <button type="button" className="submit-btn" onClick={() => setConfirmState(null)}>
                  {t('common.close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Admin-only: een al ingepland schoonmaakmoment alsnog aan een
          (nog ongeplande) boeking van dezelfde woning koppelen of
          ontkoppelen - zie handleCleaningBarClick/handleLinkBooking. */}
      {linkBookingModalData && (
        <div className="modal-overlay" onClick={() => setLinkBookingModalData(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('calendar.cleaningInfoTitle')}</h2>
              <button className="modal-close" onClick={() => setLinkBookingModalData(null)}>✖</button>
            </div>
            <div className="modal-body">
              <p style={{ whiteSpace: 'pre-line' }}>
                {t('calendar.cleaningInfo', {
                  start: formatTimeOnly(linkBookingModalData.start, locale),
                  end: formatTimeOnly(linkBookingModalData.end, locale)
                })}
                {`\n${t('calendar.statusLine', { status: statusLabels[linkBookingModalData.status] || linkBookingModalData.status })}`}
                {linkBookingModalData.cleanerNames ? `\n${t('calendar.cleaner')}: ${linkBookingModalData.cleanerNames}` : ''}
              </p>
            </div>
            <div className="form-group">
              <label>{t('admin.taskLinkBookingLabel')}</label>
              <select
                value={linkBookingModalData.bookingId}
                onChange={(e) => setLinkBookingModalData({ ...linkBookingModalData, bookingId: e.target.value })}
              >
                <option value="">{t('admin.taskLinkBookingNone')}</option>
                {unscheduledBookings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {new Date(b.checkIn).toLocaleDateString('nl-NL')} - {new Date(b.checkOut).toLocaleDateString('nl-NL')}
                    {b.guestName ? ` (${b.guestName})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-buttons">
              {canDeleteCleaning({ ...linkBookingModalData, dbId: linkBookingModalData.taskId, startDate: linkBookingModalData.start }) && (
                <button
                  type="button"
                  className="action-icon-btn action-icon-delete"
                  onClick={() => {
                    const taskId = linkBookingModalData.taskId;
                    setLinkBookingModalData(null);
                    askConfirm(t('calendar.deleteCleaningPrompt'), () => handleDeleteCleaning(taskId));
                  }}
                >
                  {t('common.delete')}
                </button>
              )}
              <button type="button" className="cancel-btn" onClick={() => setLinkBookingModalData(null)}>
                {t('common.cancel')}
              </button>
              {onEditCleaning && (
                <button
                  type="button"
                  className="action-btn"
                  onClick={() => { const id = linkBookingModalData.taskId; setLinkBookingModalData(null); onEditCleaning(id); }}
                >
                  {t('common.edit')}
                </button>
              )}
              <button
                type="button"
                className="submit-btn"
                onClick={() => handleLinkBooking(linkBookingModalData.taskId, linkBookingModalData.bookingId)}
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PropertyCalendar;
